const StorageKey = 'support_chat_head_colors';
const MaxStoredEntries = 200;

// Every shade reaches at least 4.5:1 against the white initials, checked with the WCAG formula.
// The mockup palette (indigo-500, pink-500, amber-500) sits below that threshold, so the darker
// 600/700 variants are used instead.
const Palette = [
  '#4F46E5',
  '#DB2777',
  '#B45309',
  '#047857',
  '#0369A1',
  '#7C3AED',
  '#BE123C',
  '#15803D',
  '#C2410C',
  '#0F766E',
];

type ColorMap = Record<number, string>;

function load(): ColorMap {
  try {
    const raw = localStorage.getItem(StorageKey);
    return raw ? JSON.parse(raw) as ColorMap : {};
  } catch {
    // Corrupted entry is not worth crashing the chat over; start from scratch.
    return {};
  }
}

function save(map: ColorMap): void {
  const ids = Object.keys(map).map(Number).sort((a, b) => a - b);
  // Ticket ids grow monotonically, so the lowest ones are the oldest conversations.
  const pruned = ids.slice(Math.max(0, ids.length - MaxStoredEntries));
  const next: ColorMap = {};
  pruned.forEach((id) => { next[id] = map[id]; });
  localStorage.setItem(StorageKey, JSON.stringify(next));
}

function pickColor(taken: string[]): string {
  const free = Palette.filter((color) => !taken.includes(color));
  const pool = free.length > 0 ? free : Palette;
  return pool[Math.floor(Math.random() * pool.length)];
}

// Resolves a stable colour per conversation. Colours already used by the other conversations on
// screen are avoided first, so two chat heads visible at once never share a shade unless there are
// more conversations than palette entries.
export function resolveSupportChatHeadColors(ticketIds: number[]): ColorMap {
  const stored = load();
  const result: ColorMap = {};
  const taken: string[] = [];
  let changed = false;

  ticketIds.forEach((id) => {
    const existing = stored[id];
    if (existing && Palette.includes(existing)) {
      result[id] = existing;
      taken.push(existing);
      return;
    }
    const color = pickColor(taken);
    stored[id] = color;
    result[id] = color;
    taken.push(color);
    changed = true;
  });

  if (changed) save(stored);

  return result;
}
